import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_LANGUAGE, LOCALE_KEY } from '~/i18n/constants';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '~/i18n/loadLocale';
import { getFormattingLocale } from '~/i18n/utils';
import { StorageService } from '~/services/StorageService/StorageService';

export interface State {
  locale: string;
  language: SupportedLanguage;
  currency: string;
}

const getSafeLanguage = () => {
  const stored = StorageService.getItem('language');
  if (
    !stored ||
    stored === 'null' ||
    !SUPPORTED_LANGUAGES[stored as SupportedLanguage]
  ) {
    return DEFAULT_LANGUAGE;
  }

  //At this point, it's safe to cast to SupportedLanguage as we have explicitly checked on it
  return stored as SupportedLanguage;
};

const initialState: State = {
  locale: getFormattingLocale(),
  language: getSafeLanguage(),
  currency: 'USD',
};

export default createSlice({
  name: 'i18n',
  initialState,
  selectors: {
    selectLanguage: (state: State) => state.language,
    selectLocale: (state: State) => state.locale,
    //TODO: do not use. Requires BE implementation for currency conversion, then we can use the currency prop in i18nProvider
    selectCurrency: (state: State) => state.currency,
  },
  reducers: {
    setLocale: (state: State, action: PayloadAction<string>) => {
      if (action.payload === 'system-default') {
        StorageService.removeItem(LOCALE_KEY);
        state.locale = getFormattingLocale();
      } else {
        StorageService.setItem(LOCALE_KEY, action.payload);
        state.locale = action.payload;
      }
    },

    setLanguage: (state: State, action: PayloadAction<SupportedLanguage>) => {
      StorageService.setItem('language', action.payload);
      state.language = action.payload;
    },
    setCurrency: (state: State, action: PayloadAction<string>) => {
      state.currency = action.payload;
    },

    resetI18n: () => {
      return initialState;
    },
  },
});
